import { Store } from 'redux'
import { Entity } from '../../app/entity'
import { ApplicationState } from '../../redux/ApplicationState'
import { EventsState } from '../../redux/events/EventsState'

/**
 * Loader classes like this consilidate all loading events for a domain
 * concept.
 *
 * They define one (or multiple) load() methods which execute an AJAX call
 * through the Api. You may implement parameter remapping and cache checks in
 * here. For the loaded date there should be a dispatched action through Redux.
 *
 * The Loader also defines a (static) method which handles its own actions and
 * applies the corresponding changes to the global store state.
 */
const Loader = function(store: Store<ApplicationState>, api) {
    this.store = store
    this.api = api

    this.loadEvent = parameters => {
        if (!('event' in parameters)) {
            return
        }

        const globalState = this.store.getState().events
        if (globalState.events[+parameters.event] && globalState.events[+parameters.event].isCurrent()) {
            return
        }

        this.api.request(
            'GET',
            'XhprofProfilerBundle.Event.showData',
            parameters,
            data => {
                this.store.dispatch({
                    type: 'Event.showData.success',
                    id: +parameters.event,
                    data,
                })

                if (data.event.errorId) {
                    parameters.error = data.event.errorId
                    this.api.trigger('XhprofErrorBundle.Errors.details', parameters, parameters.error)
                } else if (data.event?.dataCache?.incidentId) {
                    parameters.error = data.event.dataCache.incidentId
                    parameters.level = 'incident'
                    this.api.trigger('XhprofErrorBundle.Errors.details', parameters, parameters.error)
                }
            },
            error => {
                this.store.dispatch({
                    type: 'Event.showData.error',
                    id: +parameters.event,
                    error,
                })
            },
        )
    }

    this.loadList = parameters => {
        const clonedParameters = { ...parameters }

        if (!('events' in clonedParameters)) {
            return
        }

        // pattern 'events/*?event=' is blocked by some adblockers
        // and event parameter is not used by api endpoint
        if ('event' in clonedParameters) {
            delete clonedParameters.event
        }

        this.api.trigger('XhprofProfilerBundle.Event.listData', clonedParameters, JSON.stringify(clonedParameters.events))
    }

    this.loadGraph = parameters => {
        if (!parameters.event) {
            return
        }

        this.api.triggerContinuously('XhprofProfilerBundle.Event.graph', { event: parameters.event }, parameters.event)
    }

    this.loadGraphForIncident = parameters => {
        if (!parameters.event) {
            return
        }

        this.api.triggerContinuously('XhprofProfilerBundle.Event.graph', { event: parameters.event, type: 'incident' }, parameters.event)
    }

    this.loadCompareRange = parameters => {
        if (!parameters.event) {
            return
        }

        // A fixed comparison of two timeframes, requested once and never re-polled -
        // even when one of the ranges is "now", the screen must not live-update.
        this.api.trigger(
            'XhprofProfilerBundle.CompareRange.data',
            {
                event: parameters.event,
                ts_before: parameters.ts_before,
                m_before: parameters.m_before,
                ts_after: parameters.ts_after,
                m_after: parameters.m_after,
                s: parameters.s || null,
                env: parameters.env || null,
            },
            parameters.event,
        )
    }
}

const initialGlobalState: EventsState = {
    list: {},
    events: {},
    eventGraphs: {},
}

Loader.handleAction = (globalState: EventsState = initialGlobalState, action): EventsState => {
    let clonedGlobalState = {}

    switch (action.type) {
        case 'TIDEWAYS_ROUTE':
            return {
                list: {},
                events: {},
                eventGraphs: {},
            }
        case 'Event.listData.success':
            clonedGlobalState = { ...globalState.list }
            clonedGlobalState[action.id] = new Entity(action.data, 300)
            return {
                ...globalState,
                list: clonedGlobalState as EventsState['list'],
            }
        case 'Event.listData.error':
            clonedGlobalState = { ...globalState.list }
            clonedGlobalState[action.id] = new Entity().setError(action.error)

            return {
                ...globalState,
                list: clonedGlobalState as EventsState['list'],
            }
        case 'Event.showData.success':
            clonedGlobalState = { ...globalState.events }
            clonedGlobalState[action.id] = new Entity(
                { event: action.data.event, relatedAlertActionExecutions: action.data.relatedAlertActionExecutions },
                action.data.event.errorId ? 0 : 300,
            )

            return {
                ...globalState,
                events: clonedGlobalState as EventsState['events'],
            }
        case 'Event.showData.error':
            clonedGlobalState = { ...globalState.events }
            clonedGlobalState[action.id] = new Entity().setError(action.error)

            return {
                ...globalState,
                events: clonedGlobalState as EventsState['events'],
            }
        case 'Event.graph.success':
            clonedGlobalState = { ...globalState.eventGraphs }
            clonedGlobalState[action.id] = new Entity(action.data, 300)

            return {
                ...globalState,
                eventGraphs: clonedGlobalState,
            }
        case 'Event.graph.error':
            clonedGlobalState = { ...globalState.eventGraphs }
            clonedGlobalState[action.id] = new Entity().setError(action.error)

            return {
                ...globalState,
                eventGraphs: clonedGlobalState,
            }
        case 'CompareRange.data.success':
            clonedGlobalState = { ...globalState.eventGraphs }
            clonedGlobalState[action.id] = new Entity(action.data, 300)

            return {
                ...globalState,
                eventGraphs: clonedGlobalState,
            }
        case 'CompareRange.data.error':
            clonedGlobalState = { ...globalState.eventGraphs }
            clonedGlobalState[action.id] = new Entity().setError(action.error)

            return {
                ...globalState,
                eventGraphs: clonedGlobalState,
            }
    }

    return globalState
}

export default Loader
